import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import type { PageSurfaceTab } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import {
  DeleteConfirmModalComponent,
  type DeleteConfirmVariant,
} from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import {
  SubscriptionApiService,
  type SubscriptionListPayload,
  type SubscriptionRow,
  type SubscriptionType,
} from 'src/app/core/services/subscription-api.service';
import {
  TenantOnboardingApiService,
  type CatalogRow,
} from 'src/app/core/services/tenant-onboarding-api.service';
import {
  SubscriptionCardsComponent,
  type SubscriptionListView,
} from './subscription-cards/subscription-cards.component';

/** Stored under `no_of_subscription_models` keyed by product slug. */
export type SubscriptionModelQuota = {
  product_id: number;
  product_name: string;
  no_of_models: number;
};

@Component({
  selector: 'app-manage-subscriptions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    MaterialModule,
    IconModule,
    ListModuleSurfaceComponent,
    PageSurfaceHeaderComponent,
    SubscriptionCardsComponent,
    HasPermissionPipe,
    RestoreConfirmModalComponent,
    DeleteConfirmModalComponent,
  ],
  templateUrl: './manage-subscriptions.component.html',
  styleUrl: './manage-subscriptions.component.css',
})
export class ManageSubscriptionsComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly subscriptionApi = inject(SubscriptionApiService);
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  view: SubscriptionListView = 'active';
  rows: SubscriptionRow[] = [];
  loading = false;
  errorMessage = '';

  typeFilter: '' | SubscriptionType = '';

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: {
        placeholder: 'Search by plan name or type…',
        deletedPlaceholder: 'Search deleted plans…',
      },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  page = 1;
  readonly pageSize = 9;
  meta: SubscriptionListPayload['meta'] | null = null;

  openActionMenuKey: string | null = null;

  showFormModal = false;
  formMode: 'create' | 'edit' = 'create';
  showDeleteModal = false;
  deleteVariant: DeleteConfirmVariant = 'soft-delete';
  showRestoreModal = false;
  formLoading = signal(false);
  deleteLoading = signal(false);
  restoreLoading = signal(false);
  modalError = signal<string | null>(null);

  selected: SubscriptionRow | null = null;

  tenantOptions: Array<{ id: number; tenant_name: string | null; tenant_subdomain: string | null }> =
    [];
  tenantDropdownOpen = false;
  products: CatalogRow[] = [];
  productsLoading = false;

  form: FormGroup = this.fb.group({
    subscription_name: ['', [Validators.required]],
    subscription_type: ['STANDARD' as SubscriptionType, [Validators.required]],
    subscription_price: [null as number | null],
    validity_days: [null as number | null],
    is_active: [true],
    tenant_ids: [[] as number[]],
    model_counts: this.fb.group({}),
  });

  readonly surfaceTabs: PageSurfaceTab[] = [
    { key: 'active', label: 'Active' },
    { key: 'deleted', label: 'Deleted' },
  ];

  get modelCountsGroup(): FormGroup {
    return this.form.get('model_counts') as FormGroup;
  }

  ngOnInit(): void {
    this.loadRows();
    this.loadTenants();
    this.loadProducts();
    this.form.get('subscription_type')?.valueChanges.subscribe((type) => {
      const tenantCtrl = this.form.get('tenant_ids');
      if (type === 'CUSTOM') {
        tenantCtrl?.setValidators([Validators.required, Validators.minLength(1)]);
      } else {
        tenantCtrl?.clearValidators();
        tenantCtrl?.setValue([]);
        this.tenantDropdownOpen = false;
      }
      tenantCtrl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  get emptyMessage(): string {
    return this.view === 'deleted'
      ? 'No deleted subscriptions match your filters.'
      : 'No subscriptions match your filters.';
  }

  get formModalTitle(): string {
    return this.formMode === 'create' ? 'Add subscription' : 'Edit subscription';
  }

  get isCustomType(): boolean {
    return this.form.get('subscription_type')?.value === 'CUSTOM';
  }

  get selectedTenantIds(): number[] {
    const ids = this.form.get('tenant_ids')?.value;
    return Array.isArray(ids) ? ids : [];
  }

  get selectedTenantsLabel(): string {
    const selected = this.selectedTenantIds;
    if (!selected.length) return '';
    const labels = this.tenantOptions
      .filter((t) => selected.includes(t.id))
      .map((t) => this.tenantOptionLabel(t));
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
  }

  tenantOptionLabel(t: {
    id: number;
    tenant_name: string | null;
    tenant_subdomain: string | null;
  }): string {
    return t.tenant_name ?? t.tenant_subdomain ?? `Client #${t.id}`;
  }

  isTenantSelected(id: number): boolean {
    return this.selectedTenantIds.includes(id);
  }

  toggleTenantDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.tenantDropdownOpen = !this.tenantDropdownOpen;
  }

  toggleTenant(id: number): void {
    const current = this.selectedTenantIds;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    this.form.get('tenant_ids')?.setValue(next);
    this.form.get('tenant_ids')?.markAsDirty();
    this.form.get('tenant_ids')?.updateValueAndValidity();
  }

  onSurfaceTabKey(key: string): void {
    const next = key === 'deleted' ? 'deleted' : 'active';
    this.setView(next);
  }

  setView(mode: SubscriptionListView): void {
    if (this.view === mode) return;
    this.closeActionMenu();
    this.closeRestoreModal();
    this.closeDeleteModal();
    this.view = mode;
    this.page = 1;
    this.listFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
    this.typeFilter = '';
    this.loadRows();
  }

  loadRows(): void {
    this.loading = true;
    this.errorMessage = '';
    this.subscriptionApi
      .listSubscriptions({
        page: this.page,
        page_size: this.pageSize,
        searchText: this.listFilters.searchQuery.trim() || undefined,
        status: this.view === 'deleted' ? 'deleted' : 'active',
        subscription_type: this.typeFilter || undefined,
        sortColumn: this.view === 'deleted' ? 'deleted_at' : 'created_at',
        sortBy: 'DESC',
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          if (!res?.status || !res.data) {
            this.errorMessage = res?.message || 'Could not load subscriptions.';
            this.rows = [];
            this.meta = null;
            return;
          }
          this.rows = res.data.result ?? [];
          this.meta = res.data.meta ?? null;
        },
        error: (err) => {
          this.rows = [];
          this.meta = null;
          this.errorMessage = this.parseHttpError(err);
        },
      });
  }

  private loadTenants(): void {
    this.tenantApi.listTenantsDropdown().subscribe({
      next: (res) => {
        this.tenantOptions = res.data ?? [];
      },
      error: () => {
        this.tenantOptions = [];
      },
    });
  }

  private loadProducts(): void {
    this.productsLoading = true;
    this.tenantApi
      .listProductsDropdown()
      .pipe(finalize(() => (this.productsLoading = false)))
      .subscribe({
        next: (res) => {
          this.products = res.data ?? [];
          this.ensureModelCountControls();
          if (this.showFormModal && this.formMode === 'edit' && this.selected) {
            this.resetModelCounts(
              this.parseStoredModelCounts(this.selected.no_of_subscription_models),
            );
          }
        },
        error: () => {
          this.products = [];
        },
      });
  }

  /** One numeric control per active product (keyed by slug). */
  private ensureModelCountControls(): void {
    const group = this.modelCountsGroup;
    for (const key of Object.keys(group.controls)) {
      if (!this.products.some((p) => p.slug === key)) {
        group.removeControl(key);
      }
    }
    for (const product of this.products) {
      if (!group.contains(product.slug)) {
        group.addControl(
          product.slug,
          new FormControl<number | null>(null, [Validators.min(0)]),
        );
      }
    }
  }

  private resetModelCounts(values?: Record<string, number | null>): void {
    this.ensureModelCountControls();
    const patch: Record<string, number | null> = {};
    for (const product of this.products) {
      patch[product.slug] = values?.[product.slug] ?? null;
    }
    this.modelCountsGroup.patchValue(patch, { emitEvent: false });
  }

  private buildNoOfSubscriptionModels(): Record<string, SubscriptionModelQuota> | undefined {
    const out: Record<string, SubscriptionModelQuota> = {};
    for (const product of this.products) {
      const raw = this.modelCountsGroup.get(product.slug)?.value;
      if (raw === null || raw === undefined || raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) continue;
      out[product.slug] = {
        product_id: product.id,
        product_name: product.name,
        no_of_models: Math.floor(n),
      };
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  private parseStoredModelCounts(
    stored: SubscriptionRow['no_of_subscription_models'],
  ): Record<string, number | null> {
    const values: Record<string, number | null> = {};
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
      return values;
    }
    for (const product of this.products) {
      const count =
        this.readModelCount(stored[product.slug]) ??
        this.readModelCount(stored[product.name]);
      if (count != null) {
        values[product.slug] = count;
      }
    }
    return values;
  }

  /** Reads a model count from a stored quota entry (number or `{ no_of_models }`). */
  private readModelCount(entry: unknown): number | null {
    if (typeof entry === 'number' && Number.isFinite(entry)) {
      return entry;
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null;
    }
    const n = Number((entry as { no_of_models?: unknown }).no_of_models);
    return Number.isFinite(n) ? n : null;
  }

  modelFieldLabel(product: CatalogRow): string {
    return `No of ${product.name} models`;
  }

  modelFieldId(product: CatalogRow): string {
    return `sub-models-${product.slug}`;
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.loadRows();
  }

  onTypeFilterChange(value: string): void {
    this.typeFilter =
      value === 'STANDARD' || value === 'CUSTOM' ? value : '';
    this.page = 1;
    this.loadRows();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.loadRows();
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.agent-action-menu')) {
      this.closeActionMenu();
    }
    if (!target.closest('.tenant-multiselect')) {
      this.tenantDropdownOpen = false;
    }
  }

  openCreateModal(): void {
    this.formMode = 'create';
    this.selected = null;
    this.form.reset({
      subscription_name: '',
      subscription_type: 'STANDARD',
      subscription_price: null,
      validity_days: null,
      is_active: true,
      tenant_ids: [],
    });
    this.tenantDropdownOpen = false;
    this.resetModelCounts();
    this.modalError.set(null);
    this.showFormModal = true;
    if (this.products.length === 0) {
      this.loadProducts();
    }
  }

  openEditModal(row: SubscriptionRow): void {
    this.closeActionMenu();
    this.formMode = 'edit';
    this.selected = row;
    this.form.reset({
      subscription_name: row.subscription_name ?? '',
      subscription_type: ((row.subscription_type ?? 'STANDARD').toUpperCase() ===
      'CUSTOM'
        ? 'CUSTOM'
        : 'STANDARD') as SubscriptionType,
      subscription_price: row.subscription_price != null ? Number(row.subscription_price) : null,
      validity_days: row.validity_days,
      is_active: row.is_active !== false,
      tenant_ids: (row.subscriptionTenants ?? []).map((link) => link.tenant_id),
    });
    this.tenantDropdownOpen = false;
    this.resetModelCounts(this.parseStoredModelCounts(row.no_of_subscription_models));
    this.modalError.set(null);
    this.showFormModal = true;
    if (this.products.length === 0) {
      this.loadProducts();
    }
  }

  closeFormModal(): void {
    this.showFormModal = false;
    this.selected = null;
    this.tenantDropdownOpen = false;
    this.modalError.set(null);
  }

  submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.modalError.set(null);
    this.formLoading.set(true);
    const v = this.form.getRawValue();
    const models = this.buildNoOfSubscriptionModels();
    const payload = {
      subscription_name: String(v.subscription_name).trim(),
      subscription_type: v.subscription_type as SubscriptionType,
      ...(v.subscription_price != null && v.subscription_price !== ''
        ? { subscription_price: Number(v.subscription_price) }
        : {}),
      ...(v.validity_days != null && v.validity_days !== ''
        ? { validity_days: Number(v.validity_days) }
        : {}),
      is_active: !!v.is_active,
      ...(v.subscription_type === 'CUSTOM'
        ? {
            tenant_ids: Array.isArray(v.tenant_ids)
              ? v.tenant_ids.map((id: number) => Number(id)).filter((id: number) => id > 0)
              : [],
          }
        : {}),
      ...(models ? { no_of_subscription_models: models } : { no_of_subscription_models: {} }),
    };

    if (
      v.subscription_type === 'CUSTOM' &&
      (!(payload as { tenant_ids?: number[] }).tenant_ids?.length)
    ) {
      this.formLoading.set(false);
      this.modalError.set('Select at least one client for CUSTOM subscriptions.');
      return;
    }

    const req$ =
      this.formMode === 'create'
        ? this.subscriptionApi.create(payload)
        : this.subscriptionApi.update(this.selected!.id, payload);

    req$.pipe(finalize(() => this.formLoading.set(false))).subscribe({
      next: (res) => {
        if (res?.status) {
          this.closeFormModal();
          this.showToast(
            res.message ||
              (this.formMode === 'create'
                ? 'Subscription created.'
                : 'Subscription updated.'),
          );
          this.loadRows();
        } else {
          this.modalError.set(res?.message || 'Save failed.');
        }
      },
      error: (e) => this.modalError.set(this.parseHttpError(e)),
    });
  }

  confirmDelete(row: SubscriptionRow): void {
    this.closeActionMenu();
    this.modalError.set(null);
    this.deleteVariant = 'soft-delete';
    this.selected = row;
    this.showDeleteModal = true;
  }

  confirmPermanentDelete(row: SubscriptionRow): void {
    this.closeActionMenu();
    this.modalError.set(null);
    this.deleteVariant = 'permanent';
    this.selected = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selected = null;
    this.modalError.set(null);
  }

  submitDelete(): void {
    if (!this.selected) return;
    this.modalError.set(null);
    this.deleteLoading.set(true);
    const req$ =
      this.deleteVariant === 'permanent'
        ? this.subscriptionApi.hardDelete(this.selected.id)
        : this.subscriptionApi.softDelete(this.selected.id);

    req$.pipe(finalize(() => this.deleteLoading.set(false))).subscribe({
      next: (res) => {
        this.closeDeleteModal();
        this.showToast(
          res?.message ||
            (this.deleteVariant === 'permanent'
              ? 'Subscription permanently deleted.'
              : 'Subscription deleted.'),
        );
        if (this.rows.length <= 1 && this.page > 1) {
          this.page -= 1;
        }
        this.loadRows();
      },
      error: (e) => this.modalError.set(this.parseHttpError(e)),
    });
  }

  get deleteModalHeading(): string {
    return this.deleteVariant === 'permanent'
      ? 'Delete subscription permanently?'
      : 'Remove subscription?';
  }

  confirmRestore(row: SubscriptionRow): void {
    this.closeActionMenu();
    this.selected = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selected = null;
  }

  submitRestore(): void {
    if (!this.selected) return;
    this.restoreLoading.set(true);
    this.subscriptionApi
      .restore(this.selected.id)
      .pipe(finalize(() => this.restoreLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.closeRestoreModal();
          this.showToast(res?.message || 'Subscription restored.');
          this.loadRows();
        },
        error: (e) => {
          this.closeRestoreModal();
          this.showToast(this.parseHttpError(e), 'error');
        },
      });
  }

  private showToast(message: string, type: 'success' | 'error' = 'success'): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: type === 'error' ? 'snackbar-error' : undefined,
    });
  }

  private parseHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as { message: unknown }).message;
        if (typeof m === 'string') return m;
        if (Array.isArray(m)) return m.join(', ');
      }
      return err.message || 'Request failed';
    }
    if (err instanceof Error) return err.message;
    return 'Request failed';
  }
}
